import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';
import { get_api_url } from '../../../../../utils/urls';
import { post_api } from '../../../../../redux/api_funcs';
import CustomSingleSelect from '../../../../util_components/custom_single_select';
import CustomModal from '../../../../util_components/custom_modal';
import CustomRadioButton from '../../../../util_components/custom_radio_button';
import CustomLoading from '../../../../util_components/custom_loading';
import { FormActions } from '../../../../util_components/form_layout';
import { IstTimePicker } from '../../../../util_components/care_request/ist_time_picker';
import { class_start_ist_text } from '../../../../util_components/teacher_cancel_bucket_picker/bucket_rules';
import {
  is_valid_reason,
  trim_reason
} from '../../../../util_components/required_reason_textarea/reason_rules';
import { fullTeacherName } from '../../../../../utils/teacher_name';
import RequestTimeWarningStep from '../../../../util_components/request_time_warning';
import {
  client_request_time_warning,
  read_request_time_warning,
  session_booked_at,
  with_request_time_ack
} from '../../../../util_components/request_time_warning/request_time_warning_rules';
import { save_care_request } from '../../../../util_components/care_request/care_request_api';
import CareBatchWarningStep from './care_batch_warning';
import {
  after_care_save,
  build_pause_cancel_payload,
  build_pause_care_records,
  cancelled_session_uuids,
  care_records_warning,
  care_retry_payloads,
  partial_cancel_message,
  partial_cancel_outcome,
  pause_who_missing,
  save_care_records,
  student_requested_at_error
} from './pause_cancel_payload';

// nothing pre-picked: Care chooses Student or Admin before the cancel goes (Kuldeep, A-52)
export const initial_cancel_state = {
  status: 'CANCELLED',
  cancelled_by_student: false,
  cancelled_by_admin: false,
  student_requested_at: '',
  requested_error: '',
  who_error: false,
  cancel_reason: '',
  reason_error: false,
  save_error: '',
  loading: false
};

// every class type opens unpicked; the session type only decides whether the student time is asked
// eslint-disable-next-line no-unused-vars
export const cancel_state_for = (session_type) => ({ ...initial_cancel_state });

// server reason → inline text; strings only (an object as a React child crashes the app)
const cancel_error_message = (error, fallback) => {
  const d = error?.response?.data || error?.data;
  return (
    (typeof d?.reason === 'string' && d.reason) ||
    (typeof d?.exception_message === 'string' && d.exception_message) ||
    (typeof d?.message === 'string' && d.message) ||
    fallback
  );
};

function VacationPauseMembershipDetails(props) {
  const [openCancelOption, setOpenCancelOption] = useState(false);
  const [pauseDetails, setPauseDetails] = useState([]);
  const [pauseDetailsData, setPauseDetailsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelState, setCancelState] = useState(initial_cancel_state);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCancelIndex, setCurrentCancelIndex] = useState(null);
  const [cancelType, setCancelType] = useState(null);
  // Care records not saved, listed with their reason; careRetry: A-52 pay-time refusals awaiting one acknowledgement
  const [careFailed, setCareFailed] = useState([]);
  const [careRetry, setCareRetry] = useState(null);
  const [careRetryBusy, setCareRetryBusy] = useState(false);
  // A-52: the request-time warning shown in place of the form, and the acknowledgement being sent
  const [rtWarning, setRtWarning] = useState(null);
  const ackRef = useRef(null);

  useEffect(() => {
    if (props.from_date && props.to_date && props.shouldFetchData) {
      fetchMembershipDetails();
    }
  }, [props.student_uuid, props.from_date, props.to_date, props.shouldFetchData]);

  const { iana_timezone } = useSelector((state) => ({
    iana_timezone: state.home.iana_timezone
  }));

  const fetchMembershipDetails = async () => {
    const { student_uuid, from_date, to_date } = props;
    setLoading(true);
    const payload = {
      student_uuid,
      from_date,
      to_date
    };
    const url = get_api_url('get_pause_conflicts');
    try {
      const response = await post_api(url, payload, true);
      if (response?.data) {
        const { status, sessions } = response?.data;
        if (status === 'success' && sessions) {
          setPauseDetails(sessions);
          setPauseDetailsData(response.data);
          setOpenCancelOption(new Array(sessions.length).fill(false));

          if (sessions.length === 0) {
            props.onSessionListEmpty(true);
          } else {
            props.onSessionListEmpty(false);
          }
        }
      } else {
        console.warn('No data found in the response.');
      }
    } catch (error) {
      console.error('Error fetching data: ', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelScheduledClass = (index) => {
    const updatedCancelOptions = openCancelOption.map((value, i) => (i === index ? !value : false));
    setOpenCancelOption(updatedCancelOptions);
  };

  const openCancelModal = (index, type) => {
    setCurrentCancelIndex(index);
    setCancelType(type);
    setCancelState(cancel_state_for(pauseDetails[index]?.session_type));
    setRtWarning(null);
    setIsModalOpen(true);
  };

  const closeCancelModal = () => {
    setIsModalOpen(false);
    setCurrentCancelIndex(null);
    setCancelType(null);
    setRtWarning(null);
  };

  // after a cancel lands: the student's request time on each cancelled 1-on-1; a failure never undoes the cancel
  const saveCareRecords = async (sessionUuids, sessionType) => {
    const records = build_pause_care_records({
      sessionUuids,
      sessionType,
      cancelState,
      makeUuid: uuidv4
    }).map((r) => with_request_time_ack(r, ackRef.current));
    setCareFailed([]);
    setCareRetry(null);
    await saveCare(records);
  };

  // pay-time refusals wait for one acknowledgement; any other failure is listed with its reason
  const saveCare = async (records) => {
    const failed = await save_care_records(records, save_care_request);
    const { listed, retry } = after_care_save([], failed);
    setCareFailed((prev) => [...prev, ...listed]);
    setCareRetry(retry);
  };

  // one note covers every refused record; each is resent with its own warnings (pay_time_ack)
  const ackCareRetry = async (note) => {
    if (!careRetry || careRetryBusy) return;
    setCareRetryBusy(true);
    try {
      await saveCare(care_retry_payloads(careRetry.retry, note));
    } finally {
      setCareRetryBusy(false);
    }
  };

  const skipCareRetry = () => {
    if (!careRetry || careRetryBusy) return;
    setCareFailed((listed) => [...listed, ...careRetry.retry]);
    setCareRetry(null);
  };

  const renderCareRetry = () => (
    <CareBatchWarningStep
      retry={careRetry}
      busy={careRetryBusy}
      onAcknowledge={ackCareRetry}
      onSkip={skipCareRetry}
    />
  );

  // a partial cancel still records the classes that did cancel, so teacher pay sees the request time too
  const failedCancelResult = async (error, sessionType, fallback) => {
    const warning = read_request_time_warning(error);
    if (warning) return { ok: false, warning };
    const message = cancel_error_message(error, fallback);
    const partial = partial_cancel_outcome(error);
    if (!partial) return { ok: false, message };
    await saveCareRecords(partial.cancelled_session_uuids, sessionType);
    return { ok: false, message: partial_cancel_message(partial, message) };
  };

  const handleCancelAllRepeatClasses = async (index = 0) => {
    const { student_uuid, from_date, to_date } = props;
    setLoading(true);
    const sessionToCancel = pauseDetails[index];
    try {
      const payload = build_pause_cancel_payload(
        {
          student_uuid,
          repeat_session_id: sessionToCancel.repeat_session_id,
          session_type: sessionToCancel.session_type,
          from_date,
          to_date
        },
        cancelState
      );

      const url = get_api_url('cancel_repeat_instances_during_pause');
      const response = await post_api(url, with_request_time_ack(payload, ackRef.current), true);
      if (response?.data?.status === 'success') {
        await saveCareRecords(cancelled_session_uuids(response), sessionToCancel.session_type);
        const updatedPauseDetails = pauseDetails.filter(
          (session) => session.repeat_session_id !== sessionToCancel.repeat_session_id
        );
        setPauseDetails(updatedPauseDetails);
        const totalClassesCancelled = pauseDetails.filter(
          (session) => session.repeat_session_id === sessionToCancel.repeat_session_id
        ).length;
        setPauseDetailsData({
          ...pauseDetailsData,
          total_classes: pauseDetailsData.total_classes - totalClassesCancelled
        });
        const updatedCancelOptions = [...openCancelOption];
        updatedCancelOptions[index] = false;
        setOpenCancelOption(updatedCancelOptions);
        return { ok: true };
      }
      return {
        ok: false,
        message: cancel_error_message(response, 'Failed to cancel the repeat classes')
      };
    } catch (error) {
      // awaited so `finally` clears the spinner only after the Care records are saved
      return await failedCancelResult(
        error,
        sessionToCancel?.session_type,
        'Failed to cancel the repeat classes'
      );
    } finally {
      setLoading(false);
    }
  };
  const handleCancelClass = async (index = 0) => {
    setLoading(true);
    try {
      const singleSessionCancel = pauseDetails[index];
      const payload = build_pause_cancel_payload(
        { session_uuid: singleSessionCancel.session_uuid },
        cancelState
      );

      let url;
      if (singleSessionCancel.session_type === 'GROUP_SESSION_STUDENT') {
        url = get_api_url('cancel_group_session');
        // cancel_group_session reads `reason`, not `cancel_reason` — send both so the mandatory note lands
        payload.reason = trim_reason(cancelState.cancel_reason);
      } else {
        url = get_api_url('cancel_session');
      }

      const response = await post_api(url, with_request_time_ack(payload, ackRef.current), true);

      if (response?.data?.status === 'success') {
        await saveCareRecords(
          cancelled_session_uuids(response, singleSessionCancel.session_uuid),
          singleSessionCancel.session_type
        );
        const updatedPauseDetails = pauseDetails.filter((_, i) => i !== index);
        setPauseDetails(updatedPauseDetails);
        setPauseDetailsData({
          ...pauseDetailsData,
          total_classes: pauseDetailsData.total_classes - 1
        });
        const updatedCancelOptions = [...openCancelOption];
        updatedCancelOptions[index] = false;
        setOpenCancelOption(updatedCancelOptions);
        return { ok: true };
      }
      return { ok: false, message: cancel_error_message(response, 'Failed to cancel the session') };
    } catch (error) {
      const warning = read_request_time_warning(error);
      if (warning) return { ok: false, warning };
      return { ok: false, message: cancel_error_message(error, 'Failed to cancel the session') };
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAllFutureClasses = async (index = 0) => {
    setLoading(true);
    const futureSessionCancel = pauseDetails[index];
    try {
      const payload = build_pause_cancel_payload(
        {
          cancel_all_future_sessions: 1,
          repeat_session_uuid: futureSessionCancel.repeat_session_uuid
        },
        cancelState
      );

      let url;
      if (futureSessionCancel.session_type === 'GROUP_SESSION_STUDENT') {
        url = get_api_url('cancel_student_repeat_group_session');
        payload.repeat_group_session_participant_id =
          futureSessionCancel.repeat_group_session_participant_id;
      } else {
        url = get_api_url('cancel_repeat_sessions');
      }

      const response = await post_api(url, with_request_time_ack(payload, ackRef.current), true);
      if (response?.data?.status === 'success') {
        await saveCareRecords(cancelled_session_uuids(response), futureSessionCancel.session_type);
        const updatedPauseDetails = pauseDetails.filter(
          (session) => session.repeat_session_id !== futureSessionCancel.repeat_session_id
        );
        setPauseDetails(updatedPauseDetails);
        const totalClassesCancelled = pauseDetails.filter(
          (session) => session.repeat_session_id === futureSessionCancel.repeat_session_id
        ).length;
        setPauseDetailsData({
          ...pauseDetailsData,
          total_classes: pauseDetailsData.total_classes - totalClassesCancelled
        });
        const updatedCancelOptions = [...openCancelOption];
        updatedCancelOptions[index] = false;
        setOpenCancelOption(updatedCancelOptions);
        return { ok: true };
      }
      return {
        ok: false,
        message: cancel_error_message(response, 'Failed to cancel the future sessions')
      };
    } catch (error) {
      // awaited so `finally` clears the spinner only after the Care records are saved
      return await failedCancelResult(
        error,
        futureSessionCancel?.session_type,
        'Failed to cancel the future sessions'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditModalChange = (e) => {
    const { name, value } = e.target;
    if (name === 'status' && value !== 'CANCELLED') {
      setCancelState((prevState) => ({
        ...prevState,
        cancelled_by_student: false
      }));
    }
    setCancelState((prevState) => ({
      ...prevState,
      [name]: value,
      reason_error: false,
      requested_error: '',
      save_error: ''
    }));
  };
  // a pause-driven cancel is never the teacher's, so there is no "Cancelled by Teacher" here

  // ack: the request-time acknowledgement from the warning step; null on the first save
  const saveModal = async (ack = null) => {
    // reason is mandatory for payment audit
    const requestedError = student_requested_at_error(
      cancelState,
      Date.now(),
      pauseDetails[currentCancelIndex]?.session_type
    );
    const whoMissing = pause_who_missing(cancelState);
    if (!is_valid_reason(cancelState.cancel_reason) || requestedError || whoMissing) {
      setCancelState((prevState) => ({
        ...prevState,
        reason_error: !is_valid_reason(prevState.cancel_reason),
        requested_error: requestedError,
        who_error: whoMissing
      }));
      return;
    }
    const handlers = {
      single: handleCancelClass,
      future: handleCancelAllFutureClasses,
      repeat: handleCancelAllRepeatClasses
    };
    const handler = handlers[cancelType];
    if (!handler) return;
    // pre-check only a single class (one start); a series is measured by apis from its first booking
    const cls = pauseDetails[currentCancelIndex];
    const pre =
      !ack && cancelType === 'single' && cancelState.cancelled_by_student
        ? client_request_time_warning({
            requested_local: cancelState.student_requested_at,
            class_start: cls?.start_time,
            booked_at: session_booked_at(cls)
          })
        : null;
    if (pre) {
      setRtWarning(pre);
      return;
    }
    ackRef.current = ack;
    setRtWarning(null);
    // the modal stays open until the request succeeds; a 400 shows its reason inline instead of vanishing
    setCancelState((prevState) => ({ ...prevState, save_error: '' }));
    const result = await handler(currentCancelIndex);
    if (result.ok) {
      closeCancelModal();
    } else if (result.warning) {
      setRtWarning(result.warning);
    } else {
      setCancelState((prevState) => ({ ...prevState, save_error: result.message }));
    }
  };
  // Student or Admin, one at a time; picking the picked one clears it
  const toggleCancelledByStudent = () => {
    setCancelState((prevState) => ({
      ...prevState,
      cancelled_by_student: !prevState.cancelled_by_student,
      cancelled_by_admin: false,
      requested_error: '',
      who_error: false,
      status: prevState.cancelled_by_student ? prevState.status : 'CANCELLED'
    }));
  };
  const toggleCancelledByAdmin = () => {
    setCancelState((prevState) => ({
      ...prevState,
      cancelled_by_admin: !prevState.cancelled_by_admin,
      cancelled_by_student: false,
      student_requested_at: '',
      requested_error: '',
      who_error: false
    }));
  };

  function CancelledReason() {
    const session_status_values = [{ label: 'CANCELLED', value: 'CANCELLED' }];
    if (cancelState.loading) {
      return null;
    }
    const pauseStart = pauseDetails[currentCancelIndex]?.start_time;
    // the student time is typed in IST; the list above may be in the page's zone
    const pauseStartText = pauseStart ? class_start_ist_text(moment(pauseStart).valueOf()) : '';
    return (
      <CustomModal show={isModalOpen} close={closeCancelModal} variant="form">
        {/* a partial cancel keeps this dialog open, so the Care step shows here first */}
        {careRetry ? (
          renderCareRetry()
        ) : rtWarning ? (
          <RequestTimeWarningStep
            warning={rtWarning}
            requestedLocal={cancelState.student_requested_at}
            busy={loading}
            onAcknowledge={(ack) => saveModal(ack)}
            onChangeTime={() => setRtWarning(null)}
          />
        ) : (
          <>
            <h5>Edit Session Details</h5>
            <CustomSingleSelect
              label="Change status"
              options={session_status_values}
              onChange={handleEditModalChange}
              value={cancelState.status || ''}
              name="status"
            />
            <div id="pause-who-label" className="myt-form-label">
              Who cancelled? <span aria-hidden="true">*</span>
            </div>
            <div
              role="radiogroup"
              aria-labelledby="pause-who-label"
              aria-required="true"
              aria-invalid={cancelState.who_error ? 'true' : undefined}
              aria-describedby={cancelState.who_error ? 'pause-who-error' : undefined}
              className="myt-form-radios"
            >
              <CustomRadioButton
                label="Cancelled by Student"
                onRadioClick={toggleCancelledByStudent}
                selected={cancelState.cancelled_by_student}
              />
              <CustomRadioButton
                label="Cancelled by Admin"
                onRadioClick={toggleCancelledByAdmin}
                selected={cancelState.cancelled_by_admin}
              />
            </div>
            {cancelState.who_error ? (
              <p id="pause-who-error" role="alert" className="myt-form-error">
                * Choose who asked to cancel: Cancelled by Student or Cancelled by Admin
              </p>
            ) : null}
            {cancelState.cancelled_by_student &&
            pauseDetails[currentCancelIndex]?.session_type !== 'GROUP_SESSION_STUDENT' ? (
              <div className="myt-field">
                <label className="myt-form-label" htmlFor="pause-student-requested-at">
                  Student requested at (IST)
                </label>
                <IstTimePicker
                  id="pause-student-requested-at"
                  bookedAt={session_booked_at(pauseDetails[currentCancelIndex])}
                  value={cancelState.student_requested_at || ''}
                  // same input-style event the native field sent
                  onChange={(value) =>
                    handleEditModalChange({ target: { name: 'student_requested_at', value } })
                  }
                  ariaRequired="true"
                  ariaInvalid={cancelState.requested_error ? 'true' : undefined}
                  ariaDescribedBy={
                    [
                      pauseStartText ? 'pause-class-start' : '',
                      cancelState.requested_error ? 'pause-requested-at-error' : ''
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                  className="care-request-time"
                />
                {pauseStartText ? (
                  <p id="pause-class-start" className="myt-form-help">
                    {pauseStartText}
                  </p>
                ) : null}
                {cancelState.requested_error ? (
                  <p id="pause-requested-at-error" role="alert" className="myt-form-error">
                    * {cancelState.requested_error}
                  </p>
                ) : null}
              </div>
            ) : null}
            <div className="myt-field">
              <textarea
                value={cancelState.cancel_reason}
                name="cancel_reason"
                aria-label="Cancellation reason (required)"
                aria-required="true"
                aria-invalid={cancelState.reason_error ? 'true' : undefined}
                aria-describedby={cancelState.reason_error ? 'pause-reason-error' : undefined}
                onChange={handleEditModalChange}
                style={{ height: '100px' }}
                maxLength="200"
                placeholder="Enter cancellation reason (required, max 200 char)"
              />
              {cancelState.reason_error ? (
                <p id="pause-reason-error" role="alert" className="myt-form-error">
                  * A reason (at least 3 characters) is required to cancel
                </p>
              ) : null}
            </div>
            {cancelState.save_error ? (
              <p className="myt-form-error">* {cancelState.save_error}</p>
            ) : null}
            <FormActions>
              <button onClick={() => saveModal()}>Save</button>
            </FormActions>
          </>
        )}
      </CustomModal>
    );
  }
  return (
    <>
      {loading && <CustomLoading />}
      <CustomModal show={!!careRetry && !isModalOpen} close={skipCareRetry} variant="form">
        {careRetry ? renderCareRetry() : null}
      </CustomModal>
      {careFailed.length ? (
        <p className="myt-form-error">* {care_records_warning(careFailed)}</p>
      ) : null}
      {pauseDetailsData.status === 'success' && pauseDetailsData.total_classes > 0 ? (
        <div className="font-medium text-sm text-content-primary border-b border-line pb-3 mb-3">
          Unable to pause: <b> {pauseDetailsData.total_classes} </b>{' '}
          {pauseDetailsData.total_classes > 1 ? 'classes' : 'class'} scheduled during this period.
          Reschedule or cancel to proceed.
        </div>
      ) : null}
      <div className="flex flex-col gap-2.5">
        {pauseDetails.map((item, index) => (
          <div className="flex flex-wrap gap-2.5">
            <div className="flex flex-col gap-2.5 min-w-0 flex-1 basis-[280px]">
              <div className="flex flex-wrap items-center justify-between gap-2 border border-line rounded-lg p-3 w-full">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="h-8 w-8 rounded-full shrink-0">
                    <img
                      src={item.teacher_photo}
                      className="h-8 w-8 rounded-full"
                      style={{ position: 'static', width: '32px', cursor: '' }}
                    />
                  </div>

                  <div className="min-w-0">
                    <div className="font-semibold text-content-primary text-sm leading-normal break-words">
                      {item.session_type === 'GROUP_SESSION_STUDENT' ? item.gc_name : null}
                      {item.session_type === 'YOGA'
                        ? `1:1 Session with ${fullTeacherName(item.teacher_name, item.middle_name, item.last_name_full_value || item.last_name)}`
                        : null}
                      {item.session_type === 'YOGA_WITH_FRIENDS_SESSION_STUDENT'
                        ? `Yoga with Friends with ${fullTeacherName(item.teacher_name, item.middle_name, item.last_name_full_value || item.last_name)}`
                        : null}
                      {item.session_type === 'WORKSHOP_SESSION_STUDENT' ? item.workshop_name : null}
                    </div>
                    <div className="flex flex-wrap items-center text-content-secondary text-[13px] gap-x-3 gap-y-0.5 leading-[1.45]">
                      <div>
                        <b>Date: </b>
                        {moment(item.start_time).tz(iana_timezone).format('DD MMMM (dddd)')}
                      </div>
                      {item.session_type === 'YOGA' ? null : (
                        <div>
                          <b>Teacher: </b>{' '}
                          {fullTeacherName(
                            item.teacher_name,
                            item.middle_name,
                            item.last_name_full_value || item.last_name
                          )}
                        </div>
                      )}
                      <div>
                        <b>Duration: </b> {item.duration}
                      </div>
                    </div>
                  </div>
                </div>
                <button onClick={() => handleCancelScheduledClass(index)}>Cancel</button>
              </div>
            </div>
            {openCancelOption[index] ? (
              <div className="border border-line rounded-lg p-3 flex flex-col gap-2 basis-[200px] flex-1 sm:flex-none sm:w-[228px] justify-center h-fit">
                <div
                  // onClick={() => handleCancelClass(index)}
                  onClick={() => openCancelModal(index, 'single')}
                  className="font-medium text-[13px] text-brand-secondary cursor-pointer"
                >
                  Cancel this class
                </div>
                {item.repeat_group_session_participant_id === null &&
                item.repeat_session_uuid === null ? null : (
                  <>
                    <div
                      onClick={() => openCancelModal(index, 'future')}
                      className="font-medium text-[13px] text-brand-secondary cursor-pointer"
                    >
                      Cancel all future classes
                    </div>
                    <div
                      onClick={() => openCancelModal(index, 'repeat')}
                      className="font-medium text-[13px] text-brand-secondary cursor-pointer"
                    >
                      Cancel all repeat classes under this period
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {CancelledReason()}
    </>
  );
}

export default VacationPauseMembershipDetails;
